import type { ObjectField } from '@puckeditor/core'
// Relative, not the `global/` alias: that alias only exists at build time, so tsc
// reports it as an unresolved module (puck/CLAUDE.md rule 8).
import analytics from '../../../global/AnalyticsData'

/**
 * Opt a block into emitting an analytics event when it is clicked (BR-5583).
 *
 * One object field rather than four loose ones, so the panel reads as a single
 * decision — is this block tracked, and as what — instead of four unrelated
 * controls an author has to recognise as belonging together.
 *
 * Kept here rather than inside the Button so the next block that needs tracking
 * is one import, not a copy.
 */
export type AnalyticsProps = {
  eventName?: string
  eventContext?: string
  toGa4?: boolean
  toWarehouse?: boolean
}

export const analyticsField: ObjectField<AnalyticsProps> = {
  type: 'object',
  label: 'Analytics',
  objectFields: {
    eventName: {
      label: 'Event Name (leave blank for no tracking)',
      type: 'text',
      placeholder: 'module_click',
    },
    eventContext: {
      label: 'Event Context (defaults to the button label)',
      type: 'text',
      placeholder: 'hero-cta',
    },
    toGa4: {
      label: 'Send to GA4',
      type: 'radio',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    toWarehouse: {
      label: 'Send to BigQuery',
      type: 'radio',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
  },
}

export const analyticsDefaults: AnalyticsProps = {
  eventName: '',
  eventContext: '',
  toGa4: true,
  toWarehouse: true,
}

/**
 * Emit the configured event, or nothing at all when no name is set.
 *
 * Returns undefined rather than a no-op handler when there is nothing to send, so a
 * caller can tell whether it needs an onClick at all.
 *
 * `fallbackContext` is usually the block's own label: an author who names an event but
 * not a context still gets something readable in the report.
 */
export const makeAnalyticsHandler = (
  props: AnalyticsProps | undefined,
  fallbackContext?: string,
): (() => void) | undefined => {
  const eventName = props?.eventName?.trim()
  if (!eventName) return undefined

  return () => {
    const context = props?.eventContext?.trim() || fallbackContext
    // Both are synchronous enough to land before a navigation starts.
    if (props?.toGa4 !== false) {
      analytics.trackEvent(eventName, { break: { context } })
    }
    if (props?.toWarehouse !== false) {
      analytics.warehouseEvent(eventName, { context })
    }
  }
}
